/**
 * Per-tab session persistence. Mirrors the legacy widget's pattern:
 * drop a sessionStorage snapshot so refreshing the host page keeps the
 * visitor's conversation rather than starting a new one. Scoped per
 * agent publicId so previewing multiple agents doesn't cross-contaminate.
 *
 * sessionStorage (not localStorage) is deliberate — we want the scope to
 * die when the tab closes. Cross-tab persistence is a stateful identity
 * problem and belongs in slice 10 via the server session row.
 */

import type { ChatHistoryMessage } from '@showpilot/core';

export interface PendingHighlight {
  /** Substring of visible element text — used to locate it post-nav. */
  text: string;
  /** Optional reason for the highlight (logged, not user-facing). */
  reason?: string;
  /** Page URL the highlight targets. Verified post-load to avoid
   *  applying a stale highlight on the wrong page. */
  url?: string;
  /** Wall-clock ms when queued — drop the highlight if loadSession fires
   *  more than 30s later, since the visitor probably navigated to an
   *  unrelated page on their own. */
  queuedAt: number;
}

export interface SessionSnapshot {
  sessionId: string | null;
  history: ChatHistoryMessage[];
  /**
   * One-shot highlight to play after a cross-page navigation. Mirrors
   * legacy `agent-chat-v2/index.vue:345-382` where the chat would store
   * a "pendingNavigation" object before redirecting and the next-page
   * widget would dequeue + execute it. Set when the agent calls
   * `highlightElement` with a different URL; cleared once the widget
   * either applies the highlight or times out the snapshot.
   */
  pendingHighlight?: PendingHighlight;
  /**
   * One-shot chat message to dispatch on the next page load. Used by
   * the inline `sp:` link scheme — visitor clicks "[SaaS & Tech](sp:nav?url=/saas&msg=...)",
   * the widget navigates first and persists the message; the
   * next-page widget reads it on boot and dispatches as a chat send so
   * the agent's reply lands in the new page's context.
   */
  /** `hidden` = a stage direction for the agent, never a visitor bubble (arrival after an agent-driven navigation). */
  pendingMessage?: { text: string; queuedAt: number; hidden?: boolean };
  /**
   * Whether realtime voice was active when the snapshot was saved.
   * Set true on Talk Now press; cleared when the visitor explicitly
   * ends voice mode. The next-page widget reads this on boot and
   * auto-reopens the realtime WS so cross-page navigation doesn't
   * silently drop the call. The server-side orchestrator seeds the
   * fresh realtime session with prior chat_messages so the agent
   * picks up where it left off.
   */
  voiceModeActive?: boolean;
  /**
   * The Daily room the visitor was in when the snapshot was saved, so the
   * next page (after a full reload via the agent's navigate tool) can
   * RE-JOIN it instead of cold-summoning a new room + bot. chat-api mints a
   * fresh visitor token for this room as long as the bot is still alive in
   * its post-leave grace window. `expiresAt` is the room's unix-seconds
   * expiry; the widget drops the hint once it's in the past. Set on voice
   * session-ready; cleared when voice ends deliberately (End / idle / error).
   * `sig` is the server's HMAC binding this room to this session — the
   * server requires it on the rejoin request, so a stored hint without one
   * is useless and is treated as absent.
   */
  voiceRoom?: { roomName: string; roomUrl: string; expiresAt: number; sig: string };
  /**
   * The layout the visitor actually had open when the snapshot was
   * saved (icon / input / sidebar / …). Cross-page resume restores
   * EXACTLY this instead of a hardcoded default — "the rail persists
   * until minimized, and minimized stays minimized" (David
   * 2026-07-13: hardcoded SIDEBAR resume made the chrome visibly
   * change on navigation vs the page's fresh-mount view).
   */
  layout?: string;
  updatedAt: number;
}

const EMPTY: SessionSnapshot = {
  sessionId: null,
  history: [],
  updatedAt: 0,
};

const PENDING_MAX_AGE_MS = 30_000;

const KEY_PREFIX = 'showpilot:session:';

/**
 * Door scope (2026-09-09). The public tour / hub doors on the share host
 * all mount the same agent, and sessionStorage is per origin — so a tab
 * that opened tour A and then tour B resumed A's conversation on B's
 * door (A's narration and the visitor's old messages at the top of the
 * rail). A door names its scope once at mount; the customer-site widget
 * never sets one, so on-site continuity across pages is unchanged.
 */
let scope: string | null = null;

export function setSessionScope(next: string | null): void {
  scope = next && next.trim() ? next.trim() : null;
}

function keyFor(publicId: string): string {
  return scope ? `${KEY_PREFIX}${publicId}:${scope}` : KEY_PREFIX + publicId;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    // Safari private mode / sandboxed iframes throw on sessionStorage access.
    return null;
  }
}

export function loadSession(publicId: string): SessionSnapshot {
  const s = storage();
  if (!s) return { ...EMPTY };
  try {
    const raw = s.getItem(keyFor(publicId));
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as SessionSnapshot;
    if (typeof parsed !== 'object' || !parsed) return { ...EMPTY };
    let pendingHighlight: PendingHighlight | undefined;
    if (
      parsed.pendingHighlight &&
      typeof parsed.pendingHighlight === 'object' &&
      typeof parsed.pendingHighlight.text === 'string' &&
      typeof parsed.pendingHighlight.queuedAt === 'number' &&
      Date.now() - parsed.pendingHighlight.queuedAt < PENDING_MAX_AGE_MS
    ) {
      pendingHighlight = {
        text: parsed.pendingHighlight.text,
        reason:
          typeof parsed.pendingHighlight.reason === 'string'
            ? parsed.pendingHighlight.reason
            : undefined,
        url:
          typeof parsed.pendingHighlight.url === 'string'
            ? parsed.pendingHighlight.url
            : undefined,
        queuedAt: parsed.pendingHighlight.queuedAt,
      };
    }
    let pendingMessage: { text: string; queuedAt: number; hidden?: boolean } | undefined;
    if (
      parsed.pendingMessage &&
      typeof parsed.pendingMessage === 'object' &&
      typeof parsed.pendingMessage.text === 'string' &&
      typeof parsed.pendingMessage.queuedAt === 'number' &&
      Date.now() - parsed.pendingMessage.queuedAt < PENDING_MAX_AGE_MS
    ) {
      pendingMessage = {
        text: parsed.pendingMessage.text,
        queuedAt: parsed.pendingMessage.queuedAt,
        ...(parsed.pendingMessage.hidden === true ? { hidden: true } : {}),
      };
    }
    let voiceRoom: SessionSnapshot['voiceRoom'];
    if (
      parsed.voiceRoom &&
      typeof parsed.voiceRoom === 'object' &&
      typeof parsed.voiceRoom.roomName === 'string' &&
      typeof parsed.voiceRoom.roomUrl === 'string' &&
      typeof parsed.voiceRoom.expiresAt === 'number' &&
      // Without the server signature the rejoin can't be authorized, so a
      // hint missing it is worthless — treat as absent.
      typeof parsed.voiceRoom.sig === 'string' &&
      // Drop a room hint that's already expired — no point asking to rejoin
      // a dead room; the widget would just cold-fall-back anyway. 60s buffer
      // mirrors the chat-api rejoin guard.
      parsed.voiceRoom.expiresAt > Math.floor(Date.now() / 1000) + 60
    ) {
      voiceRoom = {
        roomName: parsed.voiceRoom.roomName,
        roomUrl: parsed.voiceRoom.roomUrl,
        expiresAt: parsed.voiceRoom.expiresAt,
        sig: parsed.voiceRoom.sig,
      };
    }
    return {
      sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
      history: Array.isArray(parsed.history) ? parsed.history : [],
      pendingHighlight,
      pendingMessage,
      voiceModeActive: parsed.voiceModeActive === true,
      voiceRoom,
      layout: typeof parsed.layout === 'string' ? parsed.layout : undefined,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveSession(publicId: string, snapshot: SessionSnapshot): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(
      keyFor(publicId),
      JSON.stringify({ ...snapshot, updatedAt: Date.now() }),
    );
  } catch {
    // Quota exceeded or disabled — fail silent. Widget still works.
  }
}

export function clearSession(publicId: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(keyFor(publicId));
  } catch {
    // ignore
  }
}

/**
 * Should the next page SEND a queued pending message? A hidden stage
 * direction (the arrival line after an agent-driven navigation) belongs to
 * the TEXT runtime; when a voice call is live across the page change, the
 * bot speaks on arrival itself — sending the typed direction too produced
 * two arrival lines with different facts (prod voice run 2026-10-18).
 * A visible queued message (a link the visitor clicked) is always sent.
 */
export function pendingMessageSendable(
  pending: SessionSnapshot['pendingMessage'] | undefined,
  voiceModeActive: boolean | undefined,
): boolean {
  if (!pending) return false;
  if (pending.hidden === true && voiceModeActive === true) return false;
  return true;
}
